/**
 * Map tools
 *  - Đo khoảng cách: geodesic length on the WGS84 ellipsoid (Vincenty inverse).
 *  - Đo diện tích: area / perimeter on the VN-2000 plane (Hà Nội zone, KTT 105°00′, k = 0.9999).
 *  - Kiểm tra khu đất: draw a boundary or paste a coordinate table (VN-2000 X/Y or WGS84),
 *    edit vertices (drag, insert at edge midpoints, delete), validate the ring, then run the
 *    server-side exact polygon overlay against every planning layer.
 *  - So sánh: two synchronised maps with a draggable swipe divider.
 */
import { $, esc, icon, fmt, toast, copyText, isMobile, rafThrottle, setPressed, hideAnimated, debounce, onMobileChange } from './ui.js';
import { api, isAbort } from './api.js';
import { bus, prefs } from './state.js';
import { PLAN_LAYERS, PLAN_BY_NO, HANOI_BBOX, planFrom } from './config.js';
import { PlanMap } from './map.js';
import { vincenty, ringArea, validateRing, coordsBBox, interiorPoint, bboxContains } from './core/geo.js';
import { toVN2000, fromVN2000, makePlanarProjector, classifyVN2000Pair, ZONES, DEFAULT_ZONE } from './core/vn2000.js';
import { extractNumbers } from './core/intent.js';
import * as panel from './panel.js';
import { setPlan } from './layers.js';
import { act } from './telemetry.js';

const ML = () => window.maplibregl;
const FC = (features) => ({ type: 'FeatureCollection', features });
const F = (geometry, properties = {}) => ({ type: 'Feature', properties, geometry });
const coarse = matchMedia('(pointer: coarse)');

const META = {
  measure: { icon: 'ruler', title: 'Đo khoảng cách', poly: false, source: 'measure' },
  area: { icon: 'square-dashed', title: 'Đo diện tích', poly: true, source: 'measure' },
  parcel: { icon: 'land-plot', title: 'Kiểm tra khu đất', poly: true, source: 'parcel' },
};

let pm = null;
let t = null;              // active drawing tool state
let analysedRing = null;   // ring of the parcel currently analysed (kept on the map)
const els = {};
const BTN = {};

/* ======================================================================
   public API
   ====================================================================== */

export function initTools(planMap) {
  pm = planMap;
  els.card = $('#tool-card');
  els.cmap = $('#compare-map');
  els.handle = $('#compare-handle');
  els.bar = $('#compare-bar');
  BTN.measure = $('#btn-measure');
  BTN.area = $('#btn-area');
  BTN.parcel = $('#btn-parcel');
  BTN.compare = $('#btn-compare');
  BTN.measure.addEventListener('click', () => toggle('measure'));
  BTN.area.addEventListener('click', () => toggle('area'));
  BTN.parcel.addEventListener('click', () => toggle('parcel'));
  BTN.compare.addEventListener('click', () => toggle('compare'));

  els.card.addEventListener('click', onCardClick);
  els.card.addEventListener('input', onCardInput);
  els.card.addEventListener('change', onCardChange);
  els.card.addEventListener('paste', (e) => { if (e.target.id === 'coords-input') setTimeout(() => { parseSoon.cancel(); parsePaste(); fitDraft(true); }, 0); });
  els.card.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && e.target.matches('textarea, select, input')) { e.stopPropagation(); e.target.blur(); }
  });

  initCompare();

  bus.on('tool', (d) => {
    if (d?.name !== 'parcel') return;
    if (d.mode === 'edit') startParcel({ mode: 'draw', ring: d.ring, zone: d.zone, edit: true });
    else startParcel({ mode: d.mode });
  });
  bus.on('parcel-cleared', () => { analysedRing = null; if (!t || t.name !== 'parcel') pm.setData('parcel', null); });
}

/** Map instance is ready: attach drawing handlers. */
export function attachMap() {
  const m = pm.map;
  m.on('click', onMapClick);
  m.on('dblclick', onMapDblClick);
  m.on('mousemove', onMapMove);
  m.getCanvas().addEventListener('mouseleave', () => { if (t && t.hover) { t.hover = null; drawLive(); } });
}

export const activeTool = () => t?.name || (cmp.on ? 'compare' : null);
/** True while a drawing tool owns map clicks (the info panel must not react). */
export const capturing = () => !!t;

export function toggle(name) {
  if (name === 'compare') return cmp.on ? stopCompare() : startCompare();
  if (t?.name === name) return stop();
  if (name === 'parcel') return startParcel({ mode: 'draw' });
  start(name);
}

/** Escape: cancel the innermost active thing. Returns true when handled. */
export function escape() {
  if (t) { stop(); return true; }
  if (cmp.on) { stopCompare(); return true; }
  return false;
}

/** Keyboard support while drawing (Enter = finish, Backspace / Delete = undo). */
export function handleKey(e) {
  if (!t || e.altKey) return false;
  if ((e.key === 'z' || e.key === 'Z') && (e.ctrlKey || e.metaKey) && !e.shiftKey) { e.preventDefault(); undo(); return true; }
  if (e.ctrlKey || e.metaKey) return false;
  if (e.key === 'Enter') { e.preventDefault(); finish(); return true; }
  if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); undo(); return true; }
  return false;
}

/* ======================================================================
   drawing tool lifecycle
   ====================================================================== */

function newState(name) {
  return {
    name, poly: META[name].poly, source: META[name].source,
    pts: [], closed: false, hover: null, hist: [],
    mode: 'draw', zone: DEFAULT_ZONE, fmt: 'vn', parsed: null,
    edit: false, orig: null, dirty: false, busy: false, ctl: new AbortController(),
    labels: [], live: null, vmarks: [], mids: [], lastDrag: 0, fitted: false,
  };
}

function start(name) {
  stop({ silent: true });
  t = newState(name);
  begin();
}

function startParcel({ mode = 'draw', ring = null, zone = DEFAULT_ZONE, edit = false } = {}) {
  stop({ silent: true });
  t = newState('parcel');
  t.mode = mode === 'paste' ? 'paste' : 'draw';
  t.zone = ZONES[zone] ? zone : DEFAULT_ZONE;
  if (ring?.length >= 3) {
    t.pts = ring.map((p) => [p[0], p[1]]);
    t.closed = true;
    t.edit = edit;
    t.orig = ring;
  } else if (panel.hasAnalysis()) {
    // a fresh parcel replaces the previous result
    panel.clearAnalysis();
    analysedRing = null;
  }
  begin();
  if (t.mode === 'paste' && t.pts.length) fillTextFromPts();
}

function begin() {
  const m = pm.map;
  m.doubleClickZoom.disable();
  document.body.classList.add('drawing');
  for (const k of ['measure', 'area', 'parcel']) setPressed(BTN[k], t.name === k);
  if (isMobile() && panel.isOpen()) panel.closePanel(); // free the map on small screens
  renderCard();
  changed();
  if (t.name === 'parcel' && t.mode === 'paste') setTimeout(() => $('#coords-input', els.card)?.focus(), 60);
  bus.emit('tool-state', t.name);
}

/** Stop the active drawing tool. */
export function stop({ silent = false, keepParcel = false } = {}) {
  if (!t) return;
  const s = t;
  t = null;
  s.ctl.abort();
  clearMarkers(s);
  if (s.source === 'measure') pm.setData('measure', null);
  else if (!keepParcel) drawAnalysed();
  pm.map.doubleClickZoom.enable();
  document.body.classList.remove('drawing', 'vertex-drag');
  for (const k of ['measure', 'area', 'parcel']) setPressed(BTN[k], false);
  hideAnimated(els.card, 'closing', 180).then(() => { if (!t) els.card.innerHTML = ''; });
  if (!silent) bus.emit('tool-state', null);
}

/** Show the analysed parcel outline (or nothing). */
function drawAnalysed() {
  if (analysedRing && panel.hasAnalysis()) {
    pm.setData('parcel', FC([F({ type: 'Polygon', coordinates: [[...analysedRing, analysedRing[0]]] }, { analysed: true })]));
  } else pm.setData('parcel', null);
}

/* ======================================================================
   map events
   ====================================================================== */

function onMapClick(e) {
  if (!t || t.busy) return;
  if (t.name === 'parcel' && t.mode === 'paste') return;
  const m = pm.map;
  if (t.closed) {
    if (t.name === 'parcel') return; // edit through the vertex handles
    snapshot();
    t.pts = []; t.closed = false;      // measuring tools: start a new measurement
  }
  const n = t.pts.length;
  const p = e.point;
  if (n) {
    const last = m.project(t.pts[n - 1]);
    if (Math.hypot(last.x - p.x, last.y - p.y) < 6) { if (!t.poly && n >= 2) finish(); return; }
  }
  if (t.poly && n >= 3) {
    const first = m.project(t.pts[0]);
    if (Math.hypot(first.x - p.x, first.y - p.y) < (coarse.matches ? 22 : 12)) { finish(); return; }
  }
  snapshot();
  t.pts.push([e.lngLat.lng, e.lngLat.lat]);
  t.dirty = true;
  changed();
}

function onMapDblClick(e) {
  if (!t) return;
  e.preventDefault();
  if (t.name === 'parcel' && t.mode === 'paste') return;
  finish();
}

let lastPointer = 'mouse';
addEventListener('pointerdown', (e) => { lastPointer = e.pointerType; }, { capture: true, passive: true });
addEventListener('pointermove', (e) => { lastPointer = e.pointerType; }, { capture: true, passive: true });

const onMapMove = rafThrottle((e) => {
  if (!t || t.closed || !t.pts.length || (t.name === 'parcel' && t.mode === 'paste')) return;
  if (lastPointer === 'touch') return; // a tap's emulated mousemove is not a hover: no rubber band, no 0 m tail
  t.hover = [e.lngLat.lng, e.lngLat.lat];
  drawLive();
});

/* ======================================================================
   editing operations
   ====================================================================== */

function snapshot() {
  t.hist.push({ pts: t.pts.map((p) => p.slice()), closed: t.closed });
  if (t.hist.length > 100) t.hist.shift();
}

function undo() {
  if (!t) return;
  const h = t.hist.pop();
  if (!h) { if (t.pts.length) { t.pts.pop(); t.closed = false; changed(); } return; }
  t.pts = h.pts; t.closed = h.closed;
  t.dirty = true;
  changed();
  if (t.mode === 'paste') fillTextFromPts();
}

function clearAll() {
  if (!t) return;
  if (t.pts.length) snapshot();
  t.pts = []; t.closed = false; t.hover = null; t.dirty = true;
  if (t.mode === 'paste') { const ta = $('#coords-input', els.card); if (ta) ta.value = ''; t.parsed = null; }
  changed();
}

function finish() {
  if (!t || t.closed) return;
  if (t.poly) {
    if (t.pts.length < 3) { toast('Cần ít nhất 3 đỉnh để khép vùng', { type: 'info', ms: 1800 }); return; }
  } else if (t.pts.length < 2) return;
  snapshot();
  t.closed = true;
  t.hover = null;
  changed();
}

function removeVertex(i) {
  if (!t) return;
  if (t.closed && t.pts.length <= 3) { toast('Khu đất cần tối thiểu 3 đỉnh', { type: 'info', ms: 1800 }); return; }
  snapshot();
  t.pts.splice(i, 1);
  if (t.pts.length < 3) t.closed = false;
  t.dirty = true;
  changed();
  if (t.mode === 'paste') fillTextFromPts();
}

/* ======================================================================
   geometry & metrics
   ====================================================================== */

function lineLength(pts) {
  let s = 0;
  for (let i = 1; i < pts.length; i++) s += vincenty(pts[i - 1][1], pts[i - 1][0], pts[i][1], pts[i][0]);
  return s;
}

/** Area / perimeter on the VN-2000 plane of the chosen zone. */
function planeMetrics(pts, zone) {
  if (pts.length < 2) return { area: 0, perimeter: 0, xy: [] };
  const P = makePlanarProjector(zone);
  const xy = pts.map((p) => P.forward(p[0], p[1]));
  let per = 0;
  for (let i = 1; i < xy.length; i++) per += Math.hypot(xy[i][0] - xy[i - 1][0], xy[i][1] - xy[i - 1][1]);
  if (xy.length >= 3) per += Math.hypot(xy[0][0] - xy[xy.length - 1][0], xy[0][1] - xy[xy.length - 1][1]);
  return { area: xy.length >= 3 ? ringArea(xy) : 0, perimeter: per, xy };
}

function issueOf() {
  if (!t || !t.poly || t.pts.length < 3) return null;
  const { xy } = planeMetrics(t.pts, t.zone);
  const v = validateRing(xy);
  if (v.ok) return null;
  if (v.issues.includes('self-intersection')) {
    const [i, j] = v.crossing;
    const n = t.pts.length;
    return { kind: 'cross', text: `Ranh giới tự cắt: cạnh ${i + 1}–${(i + 1) % n + 1} cắt cạnh ${j + 1}–${(j + 1) % n + 1}. Kiểm tra thứ tự các đỉnh.` };
  }
  if (v.issues.includes('zero-area')) return { kind: 'zero', text: 'Các đỉnh thẳng hàng, khu đất không có diện tích.' };
  return null;
}

const outsideHanoi = (pts) => pts.findIndex((p) => !bboxContains(HANOI_BBOX, p[0], p[1]));

/* ======================================================================
   rendering (map)
   ====================================================================== */

function changed() {
  if (!t) return;
  drawLive();
  buildMarkers();
  renderLabels();
  updateCard();
}

/** Overlay + live readouts (cheap; runs on every mouse move / drag frame). */
function drawLive() {
  if (!t) return;
  const { pts, hover, closed, poly } = t;
  const feats = [];
  const tail = !closed && hover ? [...pts, hover] : pts;
  if (poly) {
    if (closed && pts.length >= 3) {
      feats.push(F({ type: 'Polygon', coordinates: [[...pts, pts[0]]] }, { draft: t.name === 'parcel' }));
    } else {
      if (tail.length >= 3) feats.push(F({ type: 'Polygon', coordinates: [[...tail, tail[0]]] }, { outline: false }));
      if (pts.length >= 2) feats.push(F({ type: 'LineString', coordinates: pts }));
      if (hover && pts.length) {
        const prev = [pts[pts.length - 1], hover];
        if (pts.length >= 2) prev.push(pts[0]);
        feats.push(F({ type: 'LineString', coordinates: prev }, { preview: true, draft: true }));
      }
    }
  } else {
    if (pts.length >= 2) feats.push(F({ type: 'LineString', coordinates: pts }));
    if (hover && pts.length && !closed) feats.push(F({ type: 'LineString', coordinates: [pts[pts.length - 1], hover] }, { preview: true }));
  }
  if (t.source === 'measure') pts.forEach((p, i) => feats.push(F({ type: 'Point', coordinates: p }, { first: i === 0 })));
  pm.setData(t.source, FC(feats));
  updateMetrics(tail);
  updateLiveLabel();
}

function clearMarkers(s = t) {
  if (!s) return;
  for (const mk of [...s.labels, ...s.vmarks, ...s.mids]) mk.remove();
  s.labels = []; s.vmarks = []; s.mids = [];
  if (s.live) { s.live.remove(); s.live = null; }
}

function labelMarker(lngLat, html, cls = '', anchor = 'center', offset = [0, 0]) {
  const el = document.createElement('div');
  el.className = 'm-label' + (cls ? ' ' + cls : '');
  el.innerHTML = html;
  return new (ML().Marker)({ element: el, anchor, offset }).setLngLat(lngLat).addTo(pm.map);
}

/** Segment lengths (distance tool) or the area label (polygon tools). */
function renderLabels() {
  if (!t) return;
  for (const mk of t.labels) mk.remove();
  t.labels = [];
  const { pts } = t;
  if (t.name === 'measure') {
    if (pts.length >= 2 && pts.length <= 40) {
      for (let i = 1; i < pts.length; i++) {
        const d = vincenty(pts[i - 1][1], pts[i - 1][0], pts[i][1], pts[i][0]);
        const mid = [(pts[i - 1][0] + pts[i][0]) / 2, (pts[i - 1][1] + pts[i][1]) / 2];
        t.labels.push(labelMarker(mid, fmt.dist(d), 'seg'));
      }
    }
    if (t.closed && pts.length >= 2) t.labels.push(labelMarker(pts[pts.length - 1], `${icon('ruler')}${fmt.dist(lineLength(pts))}`, 'total', 'left', [12, 0]));
  } else if (t.closed && pts.length >= 3) {
    const { area } = planeMetrics(pts, t.zone);
    const c = interiorPoint([[...pts, pts[0]]]);
    t.labels.push(labelMarker(c, fmt.area(area), 'total ' + t.name));
  }
}

/** Label that follows the cursor while drawing. */
function updateLiveLabel() {
  if (!t) return;
  const show = !t.closed && t.hover && t.pts.length && !coarse.matches;
  if (!show) { if (t.live) { t.live.remove(); t.live = null; } return; }
  const tail = [...t.pts, t.hover];
  let html;
  if (t.poly) {
    if (tail.length >= 3) html = fmt.area(planeMetrics(tail, t.zone).area);
    else html = fmt.dist(lineLength(tail));
  } else {
    const seg = vincenty(t.pts[t.pts.length - 1][1], t.pts[t.pts.length - 1][0], t.hover[1], t.hover[0]);
    html = `${fmt.dist(lineLength(tail))}${t.pts.length > 1 ? `<small>+${fmt.dist(seg)}</small>` : ''}`;
  }
  if (!t.live) t.live = labelMarker(t.hover, html, 'live', 'left', [16, 0]);
  else { t.live.setLngLat(t.hover); t.live.getElement().innerHTML = html; }
}

/** Draggable vertex handles + edge-midpoint handles (parcel tool). */
function buildMarkers() {
  if (!t) return;
  for (const mk of [...t.vmarks, ...t.mids]) mk.remove();
  t.vmarks = []; t.mids = [];
  if (t.name !== 'parcel') return;
  const n = t.pts.length;
  const numbered = t.closed && n <= 60;
  t.pts.forEach((p, i) => {
    const el = document.createElement('div');
    el.className = 'vertex-marker' + (i === 0 && !t.closed && n >= 3 ? ' first' : '');
    el.setAttribute('aria-label', `Đỉnh ${i + 1}`);
    if (numbered) el.innerHTML = `<span class="vertex-num">${i + 1}</span>`;
    if (i === 0 && !t.closed && n >= 3) el.setAttribute('data-tip', 'Nhấp để khép ranh giới');
    const mk = new (ML().Marker)({ element: el, draggable: true }).setLngLat(p).addTo(pm.map);
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      if (performance.now() - t.lastDrag < 250) return;
      if (i === 0 && !t.closed && t.pts.length >= 3) finish();
    });
    el.addEventListener('contextmenu', (e) => { e.preventDefault(); e.stopPropagation(); removeVertex(i); });
    mk.on('dragstart', () => { snapshot(); document.body.classList.add('vertex-drag'); });
    mk.on('drag', () => { const ll = mk.getLngLat(); t.pts[i] = [ll.lng, ll.lat]; dragFrame(); });
    mk.on('dragend', () => { endDrag(); });
    t.vmarks.push(mk);
  });
  if (!t.closed || n > 200) return;
  for (let i = 0; i < n; i++) {
    const a = t.pts[i], b = t.pts[(i + 1) % n];
    const el = document.createElement('div');
    el.className = 'vertex-marker mid';
    el.setAttribute('aria-label', `Thêm đỉnh giữa cạnh ${i + 1}`);
    el.setAttribute('data-tip', 'Kéo hoặc nhấp để thêm đỉnh');
    const mk = new (ML().Marker)({ element: el, draggable: true }).setLngLat([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]).addTo(pm.map);
    let idx = -1;
    mk.on('dragstart', () => { snapshot(); idx = i + 1; const ll = mk.getLngLat(); t.pts.splice(idx, 0, [ll.lng, ll.lat]); document.body.classList.add('vertex-drag'); });
    mk.on('drag', () => { if (idx < 0) return; const ll = mk.getLngLat(); t.pts[idx] = [ll.lng, ll.lat]; dragFrame(); });
    mk.on('dragend', () => { idx = -1; endDrag(); });
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      if (performance.now() - t.lastDrag < 250) return;
      snapshot();
      t.pts.splice(i + 1, 0, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]);
      t.dirty = true;
      changed();
      if (t.mode === 'paste') fillTextFromPts();
    });
    t.mids.push(mk);
  }
}

const dragFrame = rafThrottle(() => { if (t) { drawLive(); updateCard(); } });

function endDrag() {
  if (!t) return;
  t.lastDrag = performance.now();
  t.dirty = true;
  document.body.classList.remove('vertex-drag');
  changed();
  if (t.mode === 'paste') fillTextFromPts();
}

/* ======================================================================
   tool card
   ====================================================================== */

function hint() {
  const { name, pts, closed } = t;
  const touch = coarse.matches;
  const Tap = touch ? 'Chạm' : 'Nhấp';
  if (name === 'parcel' && t.mode === 'paste') return 'Dán bảng toạ độ ranh giới từ Excel, Word hoặc PDF: mỗi dòng một đỉnh.';
  if (!pts.length) return name === 'measure' ? `${Tap} lên bản đồ để đặt điểm đầu tiên.` : name === 'area' ? `${Tap} lên bản đồ để đặt đỉnh đầu tiên.` : `${Tap} lên bản đồ để đặt đỉnh đầu tiên của khu đất.`;
  if (!closed) {
    if (name === 'measure') return touch ? 'Chạm để thêm điểm · bấm “Xong” để kết thúc · “Hoàn tác” bỏ điểm cuối.' : 'Nhấp để thêm điểm · nhấp đúp hoặc Enter để kết thúc · Backspace để hoàn tác.';
    if (pts.length < 3) return `${Tap} để thêm đỉnh (cần tối thiểu 3 đỉnh).`;
    return touch ? 'Chạm vào đỉnh đầu tiên hoặc bấm “Xong” để khép vùng.' : 'Nhấp vào đỉnh đầu tiên, nhấp đúp hoặc nhấn Enter để khép vùng.';
  }
  if (name === 'parcel') return touch ? 'Giữ và kéo đỉnh để chỉnh, chạm điểm giữa cạnh để thêm đỉnh.' : 'Kéo đỉnh để chỉnh · kéo điểm giữa cạnh để thêm đỉnh · nhấp chuột phải vào đỉnh để xoá.';
  return `${Tap} lên bản đồ để bắt đầu phép đo mới.`;
}

/**
 * Example boundary (VN-2000, KTT 105°00′) in Phường Bạch Mai: it straddles two planning cells of
 * phân khu H1-4 (NO1 nhóm nhà ở hiện có / CQ5 cơ quan – trường đào tạo), so the result shows a
 * real split. Area 3.826,96 m², perimeter 242,14 m (checked by shoelace on the projected plane).
 */
const EXAMPLE = [
  '1\t2323318,42\t587502,17',
  '2\t2323321,86\t587566,35',
  '3\t2323289,04\t587571,92',
  '4\t2323262,71\t587559,48',
  '5\t2323258,93\t587503,66',
].join('\n');

function renderCard() {
  const meta = META[t.name];
  const parcel = t.name === 'parcel';
  els.card.classList.remove('closing');
  els.card.hidden = false;
  els.card.dataset.tool = t.name;
  const metricKeys = t.poly ? [['area', 'Diện tích'], ['perimeter', 'Chu vi'], ['count', 'Số đỉnh']] : [['total', 'Tổng chiều dài'], ['count', 'Số điểm'], ['seg', 'Đoạn cuối']];
  els.card.innerHTML = `
    <div class="tool-head">
      <span class="card-ico">${icon(meta.icon)}</span>
      <div class="grow"><h3>${esc(t.edit ? 'Chỉnh sửa khu đất' : meta.title)}</h3><p data-hint>${esc(hint())}</p></div>
      <button type="button" class="icon-btn ghost" data-act="close" aria-label="Đóng công cụ" data-tip="Đóng  (Esc)">${icon('x')}</button>
    </div>
    ${parcel ? `
    <div class="seg" role="tablist" aria-label="Cách nhập ranh giới">
      <button type="button" role="tab" data-mode="draw" aria-selected="${t.mode === 'draw'}">${icon('pen-line')}Vẽ trên bản đồ</button>
      <button type="button" role="tab" data-mode="paste" aria-selected="${t.mode === 'paste'}">${icon('clipboard-paste')}Nhập toạ độ</button>
    </div>` : ''}
    ${parcel && t.mode === 'paste' ? `
    <textarea class="coords-input" id="coords-input" spellcheck="false" autocomplete="off" aria-label="Bảng toạ độ các đỉnh"
      placeholder="Mỗi dòng một đỉnh: số hiệu, X, Y. Ví dụ:&#10;1   2323318,42   587502,17&#10;2   2323321,86   587566,35&#10;…&#10;Nhận cả: X Y, Y X, vĩ độ, kinh độ; dấu phẩy hoặc dấu chấm thập phân."></textarea>
    <div class="form-row">
      <label for="zone-select">Hệ toạ độ</label>
      <select class="select" id="zone-select">${Object.values(ZONES).map((z) => `<option value="${z.id}"${z.id === t.zone ? ' selected' : ''}>${esc(z.label)}</option>`).join('')}</select>
      <span class="grow"></span>
      <button type="button" class="btn sm subtle" data-act="example">${icon('lightbulb')}Dùng ví dụ</button>
    </div>
    <div class="parse-status" data-status aria-live="polite"></div>` : ''}
    <div class="tool-metrics">
      ${metricKeys.map(([k, label]) => `<div class="m"><div class="k">${label}</div><div class="v" data-m="${k}">—</div></div>`).join('')}
    </div>
    <div class="parse-status err" data-issue hidden></div>
    <div class="tool-actions" data-actions></div>`;
}

function updateMetrics(tail) {
  if (!t || els.card.hidden) return;
  const set = (k, html) => { const el = els.card.querySelector(`[data-m="${k}"]`); if (el && el.innerHTML !== html) el.innerHTML = html; };
  if (t.poly) {
    const pts = t.closed ? t.pts : tail;
    const { area, perimeter } = planeMetrics(pts, t.zone);
    set('area', pts.length >= 3 ? (t.name === 'parcel' && area < 1e6 ? `${fmt.num(area, 1)}<small>m²</small>` : fmt.area(area, { html: true })) : '—');
    set('perimeter', pts.length >= 2 ? fmt.dist(perimeter, { html: true }) : '—');
    set('count', String(t.pts.length));
    const a = els.card.querySelector('[data-m="area"]');
    if (a) a.title = pts.length >= 3 ? `${fmt.num(area / 1e4, 4)} ha · mặt phẳng ${ZONES[t.zone].label}` : '';
  } else {
    const pts = t.closed ? t.pts : tail;
    set('total', pts.length >= 2 ? fmt.dist(lineLength(pts), { html: true }) : '—');
    set('count', String(t.pts.length));
    const k = pts.length;
    set('seg', k >= 2 ? fmt.dist(vincenty(pts[k - 2][1], pts[k - 2][0], pts[k - 1][1], pts[k - 1][0]), { html: true }) : '—');
  }
}

function updateCard() {
  if (!t || els.card.hidden) return;
  const h = els.card.querySelector('[data-hint]');
  if (h) h.textContent = hint();
  const issue = issueOf();
  const out = outsideHanoi(t.pts);
  const iss = els.card.querySelector('[data-issue]');
  const msg = issue ? issue.text : out >= 0 && t.name === 'parcel' ? `Đỉnh ${out + 1} nằm ngoài phạm vi Hà Nội.` : '';
  if (iss) { iss.hidden = !msg; iss.innerHTML = msg ? `${icon('triangle-alert')}<span>${esc(msg)}</span>${issue?.kind === 'cross' && t.name === 'parcel' ? ' <button type="button" class="btn-link" data-act="autosort">Tự sắp xếp đỉnh</button>' : ''}` : ''; }
  renderActions(!!issue || out >= 0);
}

function renderActions(invalid) {
  const host = els.card.querySelector('[data-actions]');
  if (!host || !t) return;
  const { name, pts, closed, busy } = t;
  const b = (act, ic, label, cls = 'subtle', dis = false) => `<button type="button" class="btn sm ${cls}" data-act="${act}"${dis ? ' disabled' : ''}>${icon(ic)}${label}</button>`;
  const parts = [
    b('undo', 'undo-2', 'Hoàn tác', 'subtle', !t.hist.length && !pts.length),
    b('clear', 'trash', 'Xoá', 'subtle', !pts.length),
    '<span class="grow"></span>',
  ];
  if (name === 'measure') {
    if (pts.length >= 2) parts.push(b('copy', 'copy', 'Sao chép', ''));
    if (!closed && pts.length >= 2) parts.push(b('finish', 'check', 'Xong', 'primary'));
  } else if (name === 'area') {
    if (closed) { parts.push(b('copy', 'copy', 'Sao chép', '')); parts.push(b('to-parcel', 'scan-search', 'Kiểm tra quy hoạch vùng này', 'primary', invalid)); }
    else if (pts.length >= 3) parts.push(b('finish', 'check', 'Khép vùng', 'primary'));
  } else {
    if (!closed && pts.length >= 3 && t.mode === 'draw') parts.push(b('finish', 'check', 'Khép ranh', ''));
    if (t.edit && !t.dirty) parts.push(b('cancel-edit', 'x', 'Huỷ', ''));
    parts.push(busy
      ? `<button type="button" class="btn sm primary" disabled>${icon('loader-circle', 'spin')}Đang phân tích…</button>`
      : b('analyze', 'scan-search', t.edit ? 'Phân tích lại' : 'Phân tích quy hoạch', 'primary', !closed || pts.length < 3 || invalid));
  }
  const html = parts.join('');
  if (host.innerHTML !== html) host.innerHTML = html;
}

function onCardClick(e) {
  const b = e.target.closest('[data-act],[data-mode]');
  if (!b || !t) return;
  if (b.dataset.mode) { switchMode(b.dataset.mode); return; }
  switch (b.dataset.act) {
    case 'close': stop(); break;
    case 'undo': undo(); break;
    case 'clear': clearAll(); break;
    case 'finish': finish(); break;
    case 'copy': copyMeasurement(); break;
    case 'to-parcel': { const ring = t.pts.slice(); startParcel({ mode: 'draw', ring }); break; }
    case 'analyze': analyze(); break;
    case 'cancel-edit': stop(); break;
    case 'example': { const ta = $('#coords-input', els.card); ta.value = EXAMPLE; t.zone = 'hn-105'; $('#zone-select', els.card).value = 'hn-105'; parsePaste(); fitDraft(true); break; }
    case 'autosort': autoSort(); break;
  }
}

function switchMode(mode) {
  if (!t || t.name !== 'parcel' || t.mode === mode) return;
  t.mode = mode;
  t.hover = null;
  renderCard();
  if (mode === 'paste') { if (t.pts.length) fillTextFromPts(); setTimeout(() => $('#coords-input', els.card)?.focus(), 30); }
  changed();
}

const parseSoon = debounce(() => parsePaste(), 140);
function onCardInput(e) { if (e.target.id === 'coords-input') parseSoon(); }
function onCardChange(e) {
  if (e.target.id === 'zone-select' && t) {
    t.zone = e.target.value;
    if (t.mode === 'paste' && t.fmt === 'vn') parsePaste(); else changed();
  }
}

function copyMeasurement() {
  const { pts } = t;
  const lines = [];
  if (t.poly) {
    const { area, perimeter, xy } = planeMetrics(pts, t.zone);
    lines.push(`Diện tích: ${fmt.num(area, 2)} m² (${fmt.num(area / 1e4, 4)} ha)`, `Chu vi: ${fmt.num(perimeter, 2)} m`, `Hệ toạ độ: ${ZONES[t.zone].label}`, '', 'Điểm\tX (m)\tY (m)\tVĩ độ\tKinh độ');
    pts.forEach((p, i) => lines.push(`${i + 1}\t${xy[i][1].toFixed(3)}\t${xy[i][0].toFixed(3)}\t${p[1].toFixed(7)}\t${p[0].toFixed(7)}`));
  } else {
    lines.push(`Tổng chiều dài: ${fmt.num(lineLength(pts), 2)} m`, '', 'Điểm\tVĩ độ\tKinh độ');
    pts.forEach((p, i) => lines.push(`${i + 1}\t${p[1].toFixed(7)}\t${p[0].toFixed(7)}`));
  }
  copyText(lines.join('\n'), 'kết quả đo');
}

/* ======================================================================
   coordinate table parsing (paste mode)
   ====================================================================== */

const isLat = (v) => v > 20.3 && v < 21.7;
const isLng = (v) => v > 105.0 && v < 106.4;

/**
 * Parse a pasted coordinate table. Each line may contain a point number, X, Y and extra columns
 * (edge lengths…); X/Y may be swapped; numbers may use "2.326.409,512", "2326409,512" or
 * "2326409.512". WGS84 lines (lat, lng in either order) are also accepted.
 */
export function parseCoordinateText(text, zone) {
  const lines = String(text || '').split(/\r?\n/);
  const out = [];
  const skipped = [];
  let swapped = 0, vn = 0, ll = 0;
  lines.forEach((raw, li) => {
    const line = raw.trim();
    if (!line) return;
    const nums = extractNumbers(line);
    let hit = null;
    for (let k = 0; k + 1 < nums.length && !hit; k++) {
      const r = classifyVN2000Pair(nums[k], nums[k + 1]);
      if (r) { hit = { kind: 'vn', x: r.x, y: r.y }; if (r.swapped) swapped++; }
    }
    for (let k = 0; k + 1 < nums.length && !hit; k++) {
      const a = nums[k], b = nums[k + 1];
      if (isLat(a) && isLng(b)) hit = { kind: 'll', lat: a, lng: b };
      else if (isLng(a) && isLat(b)) hit = { kind: 'll', lat: b, lng: a };
    }
    if (hit) { out.push({ ...hit, line: li + 1 }); if (hit.kind === 'vn') vn++; else ll++; }
    else if (/\d/.test(line)) skipped.push(li + 1);
  });
  // all numbers on one line ("x1 y1 x2 y2 …")
  if (out.length < 3) {
    const all = extractNumbers(text).filter((v) => v >= 100000);
    const seq = [];
    for (let k = 0; k + 1 < all.length; k += 2) { const r = classifyVN2000Pair(all[k], all[k + 1]); if (!r) { seq.length = 0; break; } seq.push({ kind: 'vn', x: r.x, y: r.y, line: 1 }); }
    if (seq.length >= 3) { out.length = 0; out.push(...seq); vn = seq.length; ll = 0; skipped.length = 0; }
  }
  const pts = out.map((p) => {
    if (p.kind === 'll') return [p.lng, p.lat];
    const g = fromVN2000(p.x, p.y, zone);
    return [g.lon, g.lat];
  });
  // a closed table repeats the first point at the end
  if (pts.length > 3) {
    const a = pts[0], b = pts[pts.length - 1];
    if (Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9) { pts.pop(); out.pop(); }
  }
  // drop consecutive duplicates
  for (let i = pts.length - 1; i > 0; i--) if (Math.abs(pts[i][0] - pts[i - 1][0]) < 1e-10 && Math.abs(pts[i][1] - pts[i - 1][1]) < 1e-10) { pts.splice(i, 1); out.splice(i, 1); }
  return { pts, rows: out, skipped, swapped, fmt: vn >= ll ? 'vn' : 'll', mixed: vn > 0 && ll > 0 };
}

function parsePaste() {
  if (!t || t.name !== 'parcel' || t.mode !== 'paste') return;
  const ta = $('#coords-input', els.card);
  if (!ta) return;
  const r = parseCoordinateText(ta.value, t.zone);
  const had = t.pts.length;
  t.parsed = r;
  t.fmt = r.fmt;
  t.pts = r.pts;
  t.closed = r.pts.length >= 3;
  t.dirty = true;
  changed();
  const st = els.card.querySelector('[data-status]');
  if (st) {
    let cls = '', html = '';
    if (!ta.value.trim()) html = `${icon('info')}Chưa có toạ độ. Có thể dán trực tiếp bảng thống kê toạ độ góc ranh.`;
    else if (!r.pts.length) { cls = 'err'; html = `${icon('circle-alert')}Không nhận diện được toạ độ. Kiểm tra lại số liệu X, Y (VN-2000) hoặc vĩ độ, kinh độ.`; }
    else {
      cls = r.pts.length >= 3 ? 'ok' : '';
      const fmtName = r.fmt === 'vn' ? `VN-2000 (X, Y)${r.swapped ? ' · đã tự nhận cột Y, X đảo' : ''}` : 'WGS84 (vĩ độ, kinh độ)';
      html = `${icon(r.pts.length >= 3 ? 'check' : 'info')}Nhận ${r.pts.length} đỉnh · ${fmtName}${r.mixed ? ' · có lẫn hai định dạng' : ''}${r.skipped.length ? ` · bỏ qua dòng ${r.skipped.slice(0, 5).join(', ')}${r.skipped.length > 5 ? '…' : ''}` : ''}`;
    }
    st.className = 'parse-status' + (cls ? ' ' + cls : '');
    st.innerHTML = html;
  }
  if (had < 3 && t.pts.length >= 3) fitDraft();
}

/** Write the (edited) vertices back into the textarea, in the user's format. */
function fillTextFromPts() {
  const ta = $('#coords-input', els.card);
  if (!ta || !t) return;
  if (t.fmt === 'll') ta.value = t.pts.map((p, i) => `${i + 1}\t${p[1].toFixed(7)}\t${p[0].toFixed(7)}`).join('\n');
  else ta.value = t.pts.map((p, i) => { const v = toVN2000(p[1], p[0], t.zone); return `${i + 1}\t${v.x.toFixed(3).replace('.', ',')}\t${v.y.toFixed(3).replace('.', ',')}`; }).join('\n');
  const st = els.card.querySelector('[data-status]');
  if (st) { st.className = 'parse-status ok'; st.innerHTML = `${icon('check')}${t.pts.length} đỉnh · đã cập nhật theo ranh giới chỉnh trên bản đồ`; }
}

function fitDraft(force = false) {
  if (!t || t.pts.length < 2) return;
  if (t.fitted && !force) return;
  t.fitted = true;
  pm.fitBBox(coordsBBox(t.pts), { padding: toolPadding(), maxZoom: 19 });
}

function toolPadding() {
  const p = panel.viewPadding();
  const card = els.card.hidden ? 0 : els.card.getBoundingClientRect().height + 16;
  return { ...p, bottom: Math.max(p.bottom || 0, card) };
}

/** Re-order vertices by angle around the centroid (fixes tables listed out of order). */
function autoSort() {
  if (!t || t.pts.length < 3) return;
  const cx = t.pts.reduce((s, p) => s + p[0], 0) / t.pts.length;
  const cy = t.pts.reduce((s, p) => s + p[1], 0) / t.pts.length;
  const sorted = t.pts.slice().sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
  const P = makePlanarProjector(t.zone);
  if (!validateRing(sorted.map((p) => P.forward(p[0], p[1]))).ok) { toast('Không tự sắp xếp được, hãy kiểm tra lại thứ tự đỉnh', { type: 'warn' }); return; }
  snapshot();
  t.pts = sorted;
  t.dirty = true;
  changed();
  if (t.mode === 'paste') fillTextFromPts();
  toast('Đã sắp xếp lại thứ tự đỉnh theo chiều quanh tâm', { type: 'ok' });
}

/* ======================================================================
   analysis
   ====================================================================== */

async function analyze() {
  if (!t || t.busy || t.name !== 'parcel') return;
  if (!t.closed || t.pts.length < 3) { toast('Hãy khép ranh giới khu đất trước khi phân tích', { type: 'info' }); return; }
  const issue = issueOf();
  if (issue) { toast(issue.text, { type: 'warn', ms: 4200 }); return; }
  const out = outsideHanoi(t.pts);
  if (out >= 0) { toast(`Đỉnh ${out + 1} nằm ngoài Hà Nội. Kiểm tra hệ toạ độ hoặc thứ tự X, Y.`, { type: 'warn', ms: 4200 }); return; }
  const s = t;
  s.busy = true;
  renderActions(false);
  const ring = s.pts.map((p) => [+p[0].toFixed(8), +p[1].toFixed(8)]);
  try {
    const r = await api.analyze(ring, s.zone, { signal: s.ctl.signal });
    if (t !== s) return;
    analysedRing = ring;
    stop({ keepParcel: true });
    pm.setData('parcel', FC([F({ type: 'Polygon', coordinates: [[...ring, ring[0]]] }, { analysed: true })]));
    panel.showAnalysis(r, ring, s.zone);
  } catch (e) {
    if (isAbort(e) || t !== s) return;
    s.busy = false;
    renderActions(false);
    toast(e.message || 'Không phân tích được khu đất', { type: 'err', ms: 4500 });
  }
}

/* ======================================================================
   swipe comparison
   ====================================================================== */

const cmp = { on: false, pm2: null, b: null, x: null, dragging: false, sync: null };

function initCompare() {
  els.handle.innerHTML += '<span class="compare-label glass l"></span><span class="compare-label glass r"></span>';
  const knob = els.handle.querySelector('.compare-knob');
  const move = rafThrottle((x) => placeHandle(x));
  els.handle.addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    cmp.dragging = true;
    els.handle.setPointerCapture(e.pointerId);
    els.handle.classList.add('dragging');
  });
  els.handle.addEventListener('pointermove', (e) => { if (cmp.dragging) move(e.clientX); });
  const end = () => { cmp.dragging = false; els.handle.classList.remove('dragging'); };
  els.handle.addEventListener('pointerup', end);
  els.handle.addEventListener('pointercancel', end);
  knob.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    placeHandle(cmp.x + (e.key === 'ArrowLeft' ? -1 : 1) * (e.shiftKey ? 120 : 30));
  });
  els.bar.addEventListener('change', (e) => {
    const side = e.target.dataset.side;
    if (!side) return;
    const n = planFrom(e.target.value);
    if (n === undefined) return;
    if (side === 'a') setPlan(n);
    else { cmp.b = n; cmp.pm2?.setPlan(n); updateCompareLabels(); }
  });
  els.bar.addEventListener('click', (e) => {
    const b = e.target.closest('[data-act]');
    if (!b) return;
    if (b.dataset.act === 'close') stopCompare();
    if (b.dataset.act === 'swap') {
      const a = prefs.plan, bb = cmp.b;
      cmp.b = a;
      cmp.pm2?.setPlan(a);
      setPlan(bb);
      renderCompareBar();
    }
  });
  bus.on('plan', (n) => {
    if (!cmp.on) return;
    if (!n) { stopCompare(); return; }
    renderCompareBar();
  });
  bus.on('basemap', (id) => cmp.pm2?.setBasemap(id));
  bus.on('boundaries', (list) => cmp.pm2?.setBoundaries(list));
  bus.on('opacity', (v) => cmp.pm2?.setOpacity(v));
  bus.on('theme', () => setTimeout(() => cmp.pm2?.refreshColors(), 0));
  bus.on('panel', () => { if (cmp.on) setTimeout(() => placeHandle(cmp.x), 320); });
  addEventListener('resize', () => { if (cmp.on) placeHandle(cmp.x); });
  onMobileChange((mobile) => { if (mobile && cmp.on) stopCompare(); });
}

function leftEdge() {
  if (isMobile() || !document.body.classList.contains('has-panel')) return 0;
  return Math.round($('#left-col').getBoundingClientRect().right);
}

function placeHandle(x) {
  const lo = leftEdge() + 56, hi = innerWidth - 80;
  const v = Math.round(Math.max(lo, Math.min(hi, Number.isFinite(x) ? x : (leftEdge() + innerWidth) / 2)));
  cmp.x = v;
  els.handle.style.left = v + 'px';
  els.cmap.style.clipPath = `inset(0 0 0 ${v}px)`;
  fitCompareLabels();
}

/** Keep the side labels clear of the search card / panel and the control rail: drop the left one
 *  below the search card when it would slide under it, hide a label when its side is too narrow. */
function fitCompareLabels() {
  const l = els.handle.querySelector('.compare-label.l');
  const r = els.handle.querySelector('.compare-label.r');
  if (!l || !r || els.handle.hidden) return;
  const x = cmp.x;
  const panel = document.body.classList.contains('has-panel');
  const card = $('#search-card')?.getBoundingClientRect();
  let top = '';
  const lw = l.offsetWidth;
  const colRight = panel ? leftEdge() : card ? card.right : 0;
  let lOff = false;
  if (x - 14 - lw < colRight + 8) {
    // both labels drop together so they stay aligned
    if (!panel && card && x - 14 - lw >= 8) top = Math.round(card.bottom - els.handle.getBoundingClientRect().top + 10) + 'px';
    else lOff = true;
  }
  l.style.top = r.style.top = top;
  l.classList.toggle('off', lOff);
  const rail = $('#controls')?.getBoundingClientRect();
  r.classList.toggle('off', !!rail && x + 14 + r.offsetWidth > rail.left - 8);
}

const layerOptions = (sel) => PLAN_LAYERS.map((l) => `<option value="${l.layerNo}"${l.layerNo === sel ? ' selected' : ''}>${esc(l.short)}</option>`).join('');

function renderCompareBar() {
  els.bar.innerHTML = `
    <span class="side">${icon('columns-2')}<span>So sánh</span></span>
    <select class="select" data-side="a" aria-label="Lớp bên trái">${layerOptions(prefs.plan)}</select>
    <button type="button" class="icon-btn ghost sm" data-act="swap" aria-label="Đổi hai bên" data-tip="Đổi hai bên">${icon('arrow-left-right')}</button>
    <select class="select" data-side="b" aria-label="Lớp bên phải">${layerOptions(cmp.b)}</select>
    <button type="button" class="icon-btn ghost sm" data-act="close" aria-label="Thoát so sánh" data-tip="Thoát so sánh  (Esc)">${icon('x')}</button>`;
  updateCompareLabels();
}

function updateCompareLabels() {
  const l = els.handle.querySelector('.compare-label.l');
  const r = els.handle.querySelector('.compare-label.r');
  if (l) l.textContent = PLAN_BY_NO[prefs.plan]?.short || '';
  if (r) r.textContent = PLAN_BY_NO[cmp.b]?.short || '';
  if (cmp.on) fitCompareLabels();
}

async function startCompare() {
  if (cmp.on) return;
  if (isMobile()) { toast('Chế độ so sánh cần màn hình rộng hơn', { type: 'info' }); return; }
  if (!pm.map) return;
  cmp.on = true;
  act('tool', 'compare');
  setPressed(BTN.compare, true);
  if (!prefs.plan) setPlan(654);
  if (!cmp.b || cmp.b === prefs.plan) cmp.b = prefs.plan === 895 ? 654 : 895;
  els.cmap.hidden = false;
  els.handle.hidden = false;
  els.bar.classList.remove('closing');
  els.bar.hidden = false;
  document.body.classList.add('comparing');
  renderCompareBar();
  placeHandle(cmp.x);
  const m = pm.map;
  const c = m.getCenter();
  const pm2 = new PlanMap(els.cmap, {
    view: { lat: c.lat, lng: c.lng, zoom: m.getZoom(), bearing: m.getBearing() },
    basemap: pm.basemap, plan: cmp.b, boundaries: prefs.boundaries, opacity: prefs.opacity,
    interactive: false, controls: false,
  });
  cmp.pm2 = pm2;
  // mirror the vector overlays (selection, parcel, measurements) onto the right-hand map
  pm.setData = (k, fc) => { PlanMap.prototype.setData.call(pm, k, fc); if (cmp.pm2 === pm2) pm2.setData(k, fc); };
  try { await pm2.ready; } catch { if (cmp.pm2 === pm2) { stopCompare(); toast('Không mở được chế độ so sánh', { type: 'err' }); } return; }
  if (cmp.pm2 !== pm2) return;
  for (const k of Object.keys(pm.data)) pm2.setData(k, pm.data[k]);
  cmp.sync = () => pm2.map.jumpTo({ center: m.getCenter(), zoom: m.getZoom(), bearing: m.getBearing(), pitch: 0, padding: m.getPadding() });
  cmp.sync();
  m.on('move', cmp.sync);
  els.cmap.classList.add('ready');
  if (!sessionStorage.getItem('qh.cmp.tip')) {
    showCompareTip();
    try { sessionStorage.setItem('qh.cmp.tip', '1'); } catch { /* ignore */ }
  }
}

/** One-time hint right under the knob (where the action is), gone after a few seconds or on first drag. */
function showCompareTip() {
  els.handle.querySelector('.compare-tip')?.remove();
  const tip = document.createElement('span');
  tip.className = 'compare-tip';
  tip.setAttribute('role', 'status');
  tip.textContent = 'Kéo thanh để so sánh hai lớp';
  els.handle.append(tip);
  let t = 0;
  const done = () => {
    clearTimeout(t);
    els.handle.removeEventListener('pointerdown', done);
    tip.classList.add('out');
    setTimeout(() => tip.remove(), 240);
  };
  t = setTimeout(done, 4200);
  els.handle.addEventListener('pointerdown', done);
}

function stopCompare() {
  if (!cmp.on) return;
  cmp.on = false;
  setPressed(BTN.compare, false);
  if (cmp.sync) pm.map.off('move', cmp.sync);
  cmp.sync = null;
  delete pm.setData; // back to the prototype method
  const pm2 = cmp.pm2;
  cmp.pm2 = null;
  try { pm2?.map?.remove(); } catch { /* ignore */ }
  els.cmap.classList.remove('ready');
  els.cmap.hidden = true;
  els.cmap.innerHTML = '';
  els.cmap.style.clipPath = '';
  els.handle.hidden = true;
  els.handle.querySelector('.compare-tip')?.remove();
  document.body.classList.remove('comparing');
  hideAnimated(els.bar, 'closing', 180);
}
